import { inject, Injectable, signal } from '@angular/core';
import {
  ApiResourceLocation,
  base64Encode,
  Dictionary,
  SOFTLINE_SERVICE_HTTP,
  Store,
} from '@softline/core';
import { Kopfau, PmsTaetigkeit } from '../types/kopfau';
import {
  SOFTLINE_API_BEWEAU_LOAD_PICTURE,
  SOFTLINE_API_BEWEAU_PICTURE,
  SOFTLINE_API_KOPFAU_GOLDSCHMIEDE_ASSIGN,
  SOFTLINE_API_KOPFAU_SCAN_ADD,
  SOFTLINE_FEATURE_GOLDSCHMIEDE_AUFTRAG,
} from '@softapps/feichtinger/goldschmiede';
import { catchError, firstValueFrom, lastValueFrom, switchMap, throwError } from 'rxjs';
import { LabelType, showRequestErrors } from '@softline/application';
import { map } from 'rxjs/operators';
import { TimeReportService } from './time-report.service';

@Injectable()
export class AuftragService {
  private readonly httpService = inject(SOFTLINE_SERVICE_HTTP);
  private readonly store = inject(Store);
  private readonly timeReportService = inject(TimeReportService);

  private readonly _auftraege = signal<Kopfau[]>([]);
  private readonly _loading = signal<boolean>(false);

  readonly auftraege = this._auftraege.asReadonly();
  readonly loading = this._loading.asReadonly();

  async loadPicture(id: string) {
    const pathParams = { id };

    const location: ApiResourceLocation = {
      path: SOFTLINE_API_BEWEAU_PICTURE,
      queryParams: { kzqualitaet: 'I' },
      pathParams,
    };

    const picture: { media: any[] } = await firstValueFrom(
      this.httpService.get<any>(location)
    );
    const encodedPicture = base64Encode(JSON.stringify(picture.media[0]));

    const location2: ApiResourceLocation = {
      path: SOFTLINE_API_BEWEAU_LOAD_PICTURE,
      pathParams: { archiveKey: encodedPicture },
    };

    /*return await lastValueFrom(this.httpService.download<string>(location2).pipe(
      filter(o => o.type === 'response'),
      map(o => (o as any)?.response)
    ));*/
  }

  async resumeAll(queryParams: Dictionary<any>): Promise<void> {
    const location: ApiResourceLocation = {
      path: SOFTLINE_FEATURE_GOLDSCHMIEDE_AUFTRAG,
      queryParams,
    };

    try {
      // this._auftraege.set(true);
      // const auftraege = await firstValueFrom(this.httpService.get<Kopfau[]>(location));
      // this._auftraege.set(auftraege);
      this._auftraege.set(this.auftraege());
    } catch (e) {
      this._loading.set(false);
      showRequestErrors(this.store, e);
    } finally {
      this._loading.set(false);
    }
  }

  scanAdd(
    labelType: LabelType,
    code: string,
    abteilungId: string | number
  ): Promise<{ alreadyAssigned: boolean; goldschmiede: Kopfau[] }> {
    const location = {
      path: SOFTLINE_API_KOPFAU_SCAN_ADD,
      pathParams: { labelType, code },
    };
    const assignLocation = (id: number) => ({
      path: `${SOFTLINE_API_KOPFAU_GOLDSCHMIEDE_ASSIGN}/${id}`,
    });

    const stream$ = this.httpService
      .get<{ id: number; [key: string]: unknown }>(location)
      .pipe(
        switchMap((result) =>
          this.httpService
            .create<unknown, { alreadyAssigned: boolean; goldschmiede: Kopfau[] }>(
              assignLocation(result.id),
              { id: result?.id, barcode: { barcode: code }, idabteilung: +abteilungId }
            )
            .pipe(
              map((o) => ({
                goldschmiede: o?.goldschmiede ?? [],
                alreadyAssigned: o?.alreadyAssigned ?? false,
              })),
              catchError((e) => {
                return throwError(e);
              })
            )
        )
      );

    return lastValueFrom(stream$);
  }

  async changeStatus(kopfau: Kopfau, taetigkeit?: PmsTaetigkeit): Promise<any | null> {
    let response: any;

    if (kopfau.status === 'PAUSIERT') {
      response = this.timeReportService.start([kopfau], taetigkeit);
    } else if (kopfau.status === 'GESTARTET') {
      response = this.timeReportService.stop([kopfau]);
    }

    return response;
  }

  async pauseMany(kopfaus: Kopfau[]): Promise<void> {
    try {
      this._loading.set(true);
      await this.timeReportService.stop(kopfaus);
      this._loading.set(false);
    } catch (e) {
      this._loading.set(false);
      showRequestErrors(this.store, e);
    } finally {
      this._loading.set(false);
    }
  }

  async startMany(kopfaus: Kopfau[]): Promise<void> {
    try {
      this._loading.set(true);
      await this.timeReportService.start(kopfaus);
      this._loading.set(false);
    } catch (e) {
      this._loading.set(false);
      showRequestErrors(this.store, e);
    } finally {
      this._loading.set(false);
    }
  }

  ngOnDestroy(): void {
    this._auftraege.set([]);
    this._loading.set(false);
  }
}
